import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import { ModificationRequestApi } from "@/config/apiClient";
import { createApiClient } from "@/config/createApiClient";
import { selectedClientServiceIdAtom } from "@/store";

const apiClient = createApiClient(ModificationRequestApi);

export const useModificationRequest = () => {
  const selectedClientServiceIdAtomValue = useAtomValue(
    selectedClientServiceIdAtom
  );

  const queryClient = useQueryClient();

  const useRefuseModificationRequest = () =>
    useMutation({
      mutationFn: (body: {
        modificationRequestId: string;
        orderId: string;
        orderItemId: string;
        comment?: string;
      }) =>
        apiClient.modificationRequestsControllerRefuseModificationRequest({
          clientServiceId: selectedClientServiceIdAtomValue,
          modificationRequestId: body.modificationRequestId,
          modificationRequestAnswerCommentCommand: { comment: body.comment },
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
        ]),
    });

  const useAcceptModificationRequest = () =>
    useMutation({
      mutationFn: (body: {
        modificationRequestId: string;
        orderId: string;
        orderItemId: string;
        comment?: string;
      }) =>
        apiClient.modificationRequestsControllerAcceptModificationRequest({
          clientServiceId: selectedClientServiceIdAtomValue,
          modificationRequestId: body.modificationRequestId,
          modificationRequestAnswerCommentCommand: { comment: body.comment },
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
        ]),
    });

  return {
    useAcceptModificationRequest,
    useRefuseModificationRequest,
  };
};
